import { useCallback, type PropsWithChildren, type ReactNode } from "react"
import { config } from "../config/Config"
import { getBusinessUnit } from "../utils/business.utils"
import {
  AuthProvider,
  useAuth,
  AvatarConfigProvider,
  type User,
  isTeacher,
  ExternalConfig
} from "@infinitaslearning/auth-client"

export interface IIdentityProviderProps {
  children: ReactNode
}

export function IdentityProvider({ children }: IIdentityProviderProps) {
  const businessUnit = getBusinessUnit()

  const isLiber = businessUnit === "Liber"

  const searchParams = new URLSearchParams(window.location.search)
  const enableIdp = searchParams.get("enableIdp")

  const handleDeeplinkLogin = useCallback(async () => {
    switch (businessUnit) {
      case "Liber": {
        if (!enableIdp) {
          window.location.href = config.externalLogin.liber
        }
        break
      }
    }
  }, [businessUnit, config.externalLogin.liber, enableIdp])

  const skipRedirect =(isLiber && !enableIdp)

  return (
    <AvatarConfigProvider
      config={{
        businessUnit,
        environment:
          config.environment === "development" ? "staging" : (config.environment as ExternalConfig["environment"]),
        platform: "SEP"
      }}
    >
      <AuthProvider redirectOnFailedLogin={!skipRedirect} onLoginFailed={handleDeeplinkLogin}>
        <ChildrenWrapper>{children}</ChildrenWrapper>
      </AuthProvider>
    </AvatarConfigProvider>
  )
}

const ChildrenWrapper = ({ children }: PropsWithChildren<unknown>) => {
  const { user } = useAuth()
  if (!user) {
    return null
  }

  window.sessionStorage.setItem("user", JSON.stringify({ ...user, isTeacher: isTeacher(user) }))
  return <>{children}</>
}

type EnhancedUser = User & { isTeacher: boolean }

export const useAuthenticatedUser = (): EnhancedUser => {
  const { user } = useAuth()
  if (!user) throw new Error("User not authenticated")
  return { ...user, isTeacher: isTeacher(user) }
}

export const getAuthenticatedUser = (): EnhancedUser => {
  const stringifiedUser = window.sessionStorage.getItem("user")
  if (!stringifiedUser) throw new Error("User not authenticated")

  try {
    return JSON.parse(stringifiedUser)
  } catch (error) {
    throw new Error("User not authenticated")
  }
}
